import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { NgToastService } from 'ng-angular-popup';
import { IttWrapperComponent } from '../itt-wrapper/itt-wrapper.component';
import { environment } from '@/app/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { GetFileIconUtilityService } from '@/app/services/utility/get-file-icon-utility.service';
import { RfpLabelService } from '../../../rfp-labels.service';

@Component({
  selector: 'app-itt-contractual-terms',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './itt-contractual-terms.component.html',
  styleUrls: ['./itt-contractual-terms.component.css'],
})
export class IttContractualTermsComponent implements OnInit {
  @Output() contractualTermsDataEmitter: EventEmitter<any> =
    new EventEmitter<any>();
  @Input() ITTDeveloperData: any;

  projectId: number;
  projectName: string;

  // For already saved files
  oldFiles = [];
  deletedFilesIds = [];

  uploadedFiles: { name: any; file: File; preview: string }[] = [];
  isDragging = false;
  contractTypeDetailsControl = new FormControl('', Validators.required);
  previousUploadedFile = null;
  base_path = environment.storageUrl;
  labels: any;

  constructor(
    private tenderingService: TenderingService,
    private toast: NgToastService,
    private ittWrapper: IttWrapperComponent,
    private route: ActivatedRoute,
    private getFileIconService: GetFileIconUtilityService,
    private rfpLabelService: RfpLabelService,
    private router: Router
  ) {}

  ngOnInit() {
    this.route.url.subscribe(() => {
      const currentRoute = this.router.url; // Get full URL path
      this.rfpLabelService.updateLabelsFromRoute(currentRoute);
    });
    this.rfpLabelService.labels$.subscribe((labels) => {
      this.labels = labels;
    });

    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    /**
     * NOTE: On edit or when we go back and forth between wizards, we will
     *  set initial value if it exists
     */
    if (this.ITTDeveloperData?.contract_type_details) {
      this.contractTypeDetailsControl.setValue(
        this.ITTDeveloperData?.contract_type_details
      );

      if (this.ITTDeveloperData?.contract_type_attachments) {
        this.ITTDeveloperData?.contract_type_attachments.forEach((oldFile) => {
          this.oldFiles.push({
            id: oldFile?.id,
            name: oldFile?.title,
            path: oldFile?.file_path,
          });
        });
      }
    }
  }

  removeOldFile(id: number) {
    this.deletedFilesIds.push(id);
    this.oldFiles = this.oldFiles.filter((file) => file.id !== id);
  }

  removeFile(index: number) {
    this.previousUploadedFile = null;
    this.uploadedFiles.splice(index, 1);
  }

  // Get the file icon from the service
  getFileIcon(file: {
    file: File;
    preview: string;
    id?: number;
    name?: string;
  }): string {
    return this.getFileIconService.getFileIcon(file);
  }

  onFileUpload(event: any, isDrop = false) {
    this.previousUploadedFile = null;
    const files = isDrop ? event.dataTransfer.files : event.target.files;

    for (let i = 0; i < files?.length; i++) {
      const file = files[i];

      this.uploadedFiles.push({
        file,
        name: file.name,
        preview: event.target.result,
      });
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer?.files) {
      this.onFileUpload(event, true);
    }
  }

  openView() {
    window.open(this.base_path + this.previousUploadedFile);
  }

  prepareDataToSubmit() {
    this.contractTypeDetailsControl.markAsTouched();

    const dataToSubmit = new FormData(); // Use FormData to include the file
    if (this.contractTypeDetailsControl.value) {
      dataToSubmit.append(
        'contract_type_details',
        this.contractTypeDetailsControl.value
      );

      if (!this.previousUploadedFile && this.uploadedFiles.length > 0) {
        this.uploadedFiles.forEach((uploadedFile) => {
          if (uploadedFile.file) {
            dataToSubmit.append('file[]', uploadedFile.file);
          }
        });
      }

      if (this.deletedFilesIds.length) {
        this.deletedFilesIds.forEach((id) => {
          dataToSubmit.append('deleted_files[]', id.toString()); // Convert number to string
        });
      }

      return dataToSubmit;
    }
  }

  moveToNextStep() {
    const dataToSubmit = this.prepareDataToSubmit();

    if (dataToSubmit) {
      /**
       * IMP: we need to send '_method', 'PUT' for every put request
       *  as browser does not fully support put requests with form data
       *  And in the http request, we will send it as post request
       */
      dataToSubmit.append('_method', 'PUT');
      this.tenderingService
        .storeDeveloperITTContractData(dataToSubmit, this.projectId)
        .subscribe({
          next: (response: any) => {
            if (response?.meta?.success) {
              this.toast.success(
                'Tender Submission Instructions saved successfully',
                'SUCCESS'
              );
              this.contractualTermsDataEmitter.emit(response?.data);
              this.ittWrapper.nextStep();
              return;
            }
            this.toast.danger(
              'Tender Submission Instructions could not be saved',
              'DANGER',
              4000
            );
          },
          error: (error: any) => {
            console.error('Failed to submit:', error);
            this.toast.danger(
              error?.error?.message || 'Submission failed',
              'ERROR'
            );
          },
        });
    } else {
      this.toast.warning('Please fill in all required fields.', 'WARNING');
    }
  }

  handleBack() {
    this.ittWrapper.previousStep();
  }
}
